import { queryService } from '../../../services/queryService';
import api from '../../../services/api';
import { useSnackbar } from '../../../components/common/Snackbar';
import { normalizeLifecycleStatus } from '../../../utils/queryListUtils';
import { normalizeId } from '../../../utils/assignedQueriesUtils';

/**
 * Hook to manage query operations for AssignedQueries page
 * @param {Function} fetchQueries - Function to refetch queries
 * @param {Function} setEscalatingId - Setter for escalating ID (from modals hook)
 * @param {Function} setAssigningId - Setter for assigning ID (from modals hook)
 * @param {Function} handleCloseEscalateDialog - Handler to close escalate dialog
 * @param {Function} handleCloseAssignDialog - Handler to close assign dialog
 * @returns {Object} Operation handlers
 */
export const useAssignedQueriesPageOperations = (
  fetchQueries,
  setEscalatingId,
  setAssigningId,
  handleCloseEscalateDialog,
  handleCloseAssignDialog
) => {
  const { showSnackbar } = useSnackbar();

  const handleConfirmEscalation = async (query, escalationData) => {
    if (!query?._id) return;
    
    setEscalatingId(query._id);
    try {
      const normalizedCurrentStatus = normalizeLifecycleStatus(query.status);
      const normalizedLabelStatus = normalizeLifecycleStatus(query.statusLabel);

      const description =
        (query.statusResponse || '').trim() ||
        'Manually escalated from Assigned Queries.';

      const escalationTargetCandidates = [
        normalizeId(query.escalatedTo),
        normalizeId(query.assignee),
      ];
      const fallbackTargetId = escalationTargetCandidates.find(Boolean) || null;

      const selectedPath = Array.isArray(escalationData.escalationPath)
        ? escalationData.escalationPath
        : [];
      const primaryTargetId =
        escalationData.primaryTargetId ||
        (selectedPath.length > 0 ? selectedPath[0].userId : null) ||
        fallbackTargetId;

      const escalationSummary =
        selectedPath.length > 0
          ? `Escalation path: ${selectedPath
              .map((item) => `${item.roleLabel || item.role} - ${item.userName}`)
              .join(' -> ')}`
          : null;

      const regionSummary = escalationData.selectedRegion
        ? `Region: ${escalationData.selectedRegion}`
        : null;

      const descriptionParts = [description, regionSummary, escalationSummary].filter(Boolean);
      const finalDescription = descriptionParts.join(' | ');

      if (!primaryTargetId) {
        showSnackbar('No eligible escalation target found. Please assign the query first.', 'error');
        setEscalatingId(null);
        return;
      }

      if (
        normalizedCurrentStatus === 'escalated' &&
        normalizedLabelStatus !== 'escalated'
      ) {
        await queryService.updateQueryStatus(query._id, {
          status: 'raised',
          description: 'Resetting lifecycle before manual escalation',
        });
      }

      await queryService.updateQueryStatus(query._id, {
        status: 'escalated',
        description: finalDescription,
        escalatedToId: primaryTargetId || undefined,
        region: escalationData.selectedRegion || undefined,
      });
      
      showSnackbar('Query escalated and notifications triggered', 'success');
      handleCloseEscalateDialog();
      fetchQueries();
    } catch (error) {
      // console.error('Failed to escalate query:', error);
      const message = error.response?.data?.message || 'Failed to escalate query';
      showSnackbar(message, 'error');
    } finally {
      setEscalatingId(null);
    }
  };

  const handleConfirmAssign = async (query, selectedAssigneeId, assignReason, assignNote) => {
    if (!query?._id) return;
    if (!selectedAssigneeId) {
      showSnackbar('Please select an assignee', 'error');
      return;
    }

    setAssigningId(query._id);
    try {
      await api.post(`/queries/${query._id}/reassign`, {
        action: 'assign',
        assigneeId: selectedAssigneeId,
        reason: assignReason?.trim() || 'Handing off to responder',
        status: 'delegated',
        statusLabel: 'delegated',
        statusResponse: assignNote?.trim() || 'Awaiting response',
      });
      showSnackbar('Query delegated successfully', 'success');
      handleCloseAssignDialog();
      fetchQueries();
    } catch (error) {
      // console.error('Failed to assign query:', error);
      const message = error.response?.data?.message || 'Failed to assign query';
      showSnackbar(message, 'error');
      setAssigningId(null);
    }
  };

  return {
    handleConfirmEscalation,
    handleConfirmAssign,
  };
};
